import { Component, EventEmitter, Input, Output } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { DropdownModule } from 'primeng/dropdown';
import { CvDbService } from 'src/app/services/cv-db/cv-db.service';

@Component({
  selector: 'app-skills',
  templateUrl: './skills.component.html',
  styleUrls: ['./skills.component.css'],
})
export class SkillsComponent {
  @Input() formGroup!: FormGroup;

  @Output() next = new EventEmitter<void>();
  @Output() previous = new EventEmitter<void>();

  langLevel = ['Beginner', 'Intermediate', 'Advanced', 'Fluent', 'Native'];

  constructor(private fb: FormBuilder, private cvDbService: CvDbService) {}
  EditMode: boolean = false;
  ngOnInit(): void {
    // Setup min skills validator
    let formData: any = localStorage.getItem('cvFormData');
    formData = JSON.parse(formData);

    this.EditMode = formData.editMode;

    this.skills.setValidators(this.minSkillsValidator(5));
    this.skills.updateValueAndValidity();

    // إضافة entries فارغة فقط إذا مافيش بيانات
    if (this.skills.length === 0) {
      for (let i = 0; i < 1; i++) {
        this.addSkill();
      }
    }

    if (this.languages.length === 0) {
      this.addLanguage();
    }

    if (this.certificates.length === 0) {
      this.addCertificat();
    }

    if (this.courses.length === 0) {
      this.addcourse();
    }

    if (this.projects.length === 0) {
      this.addProject();
    }
  }

  ngAfterViewInit() {
    // Force update validators after view init
    setTimeout(() => {
      this.skills.updateValueAndValidity();
    }, 100);
  }

  // === SKILLS ===
  get skills(): FormArray {
    return this.formGroup.get('skill') as FormArray;
  }

  addSkill(): void {
    this.skills.push(this.fb.control('', Validators.required));
  }

  removeSkill(index: number): void {
    if (this.skills.length > 1) {
      this.skills.removeAt(index);
    }
  }

  minSkillsValidator(min: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const formArray = control as FormArray;
      const filledSkills = formArray.controls.filter((ctrl) => {
        const value = ctrl.value;
        return value && typeof value === 'string' && value.trim() !== '';
      });

      return filledSkills.length >= min
        ? null
        : { minSkills: { required: min, actual: filledSkills.length } };
    };
  }

  // === COURSES ===
  get courses(): FormArray {
    return this.formGroup.get('course') as FormArray;
  }

  addcourse(): void {
    this.courses.push(this.fb.control('', Validators.required));
  }

  removeCourse(index: number): void {
    if (this.courses.length > 1) {
      this.courses.removeAt(index);
    }
  }

  // === PROJECTS ===
  get projects(): FormArray {
    return this.formGroup.get('project') as FormArray;
  }

  addProject(): void {
    const projectGroup = this.fb.group({
      name: ['', Validators.required],
      link: [''],
      role: [''],
    });
    this.projects.push(projectGroup);
  }

  removeProject(index: number): void {
    if (this.projects.length > 1) {
      this.projects.removeAt(index);
    }
  }

  // === CERTIFICATES ===
  get certificates(): FormArray {
    return this.formGroup.get('certification') as FormArray;
  }

  addCertificat() {
    const certificat = this.fb.group({
      name: ['', Validators.required],
      organization: ['', Validators.required],
      date: ['', Validators.required],
    });
    this.certificates.push(certificat);
  }

  removeCertificat(index: number): void {
    if (this.certificates.length > 1) {
      this.certificates.removeAt(index);
    }
  }

  // === LANGUAGES ===
  get languages(): FormArray {
    return this.formGroup.get('languages') as FormArray;
  }

  addLanguage() {
    const language = this.fb.group({
      name: ['', Validators.required],
      level: ['', Validators.required],
    });
    this.languages.push(language);
  }

  removeLanguage(index: number): void {
    if (this.languages.length > 1) {
      this.languages.removeAt(index);
    }
  }

  // === EVENTS ===
  onNext() {
    // Count filled skills
    const filledSkills = this.skills.controls.filter((ctrl) => {
      const value = ctrl.value;
      return value && typeof value === 'string' && value.trim() !== '';
    });

    if (filledSkills.length < 5) {
      this.skills.markAllAsTouched();

      // Scroll to skills section
      setTimeout(() => {
        const skillsSection = document.querySelector('.skills-section');
        if (skillsSection) {
          skillsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);

      return;
    }

    // Remove empty entries from all arrays before proceeding
    this.cleanupEmptyEntries();

    this.next.emit();
  }

  private cleanupEmptyEntries() {
    // Clean skills
    for (let i = this.skills.length - 1; i >= 0; i--) {
      const value = this.skills.at(i).value;
      if (!value || (typeof value === 'string' && value.trim() === '')) {
        this.skills.removeAt(i);
      }
    }

    // Clean courses
    for (let i = this.courses.length - 1; i >= 0; i--) {
      const value = this.courses.at(i).value;
      if (!value || (typeof value === 'string' && value.trim() === '')) {
        this.courses.removeAt(i);
      }
    }

    // Clean projects
    for (let i = this.projects.length - 1; i >= 0; i--) {
      const group = this.projects.at(i) as FormGroup;
      const name = group.get('name')?.value;
      if (!name || name.trim() === '') {
        this.projects.removeAt(i);
      }
    }

    // Clean certificates
    for (let i = this.certificates.length - 1; i >= 0; i--) {
      const group = this.certificates.at(i) as FormGroup;
      const name = group.get('name')?.value;
      const org = group.get('organization')?.value;
      if ((!name || name.trim() === '') && (!org || org.trim() === '')) {
        this.certificates.removeAt(i);
      }
    }

    // Clean languages
    for (let i = this.languages.length - 1; i >= 0; i--) {
      const group = this.languages.at(i) as FormGroup;
      const name = group.get('name')?.value;
      const level = group.get('level')?.value;
      if ((!name || name.trim() === '') && (!level || level.trim() === '')) {
        this.languages.removeAt(i);
      }
    }
  }

  onPrevious() {
    this.previous.emit();
  }

  isSkillsValid(): boolean {
    const filledSkills = this.skills.controls.filter((ctrl) => {
      const value = ctrl.value;
      return value && typeof value === 'string' && value.trim() !== '';
    });
    return filledSkills.length >= 5;
  }
}
